import React, { useEffect, useState, useCallback } from "react";
import {
  useGetOptionsSchemaQuery,
  OptionFieldSchema,
} from "../../api/customerFieldApi";
import { Dropdown } from "../../components/tailwind/dropdown/Dropdown";
import { Toggle } from "../../components/tailwind/toggle/Toggle";
import { uuidv4 } from "../../utils/uuidUtils";
import debounce from "lodash/debounce";

interface Props {
  processorName: string;
  currentOptions: Record<string, unknown>;
  onOptionsChange: (options: Record<string, unknown>) => void;
}

export const ProcessorOptionsForm: React.FC<Props> = ({
  processorName,
  currentOptions,
  onOptionsChange,
}) => {
  const { data, isLoading, error } = useGetOptionsSchemaQuery(processorName);
  const [localOptions, setLocalOptions] = useState<Record<string, unknown>>(
    currentOptions
  );

  // Update local state when currentOptions changes externally
  useEffect(() => {
    setLocalOptions(currentOptions);
  }, [currentOptions]);

  // Debounced save
  const debouncedSave = useCallback(
    debounce((options: Record<string, unknown>) => {
      onOptionsChange(options);
    }, 500),
    [onOptionsChange]
  );

  const handleFieldChange = (
    fieldName: string,
    value: unknown,
    immediate = false
  ): void => {
    const newOptions = { ...localOptions, [fieldName]: value };
    setLocalOptions(newOptions);
    if (immediate) {
      onOptionsChange(newOptions);
    } else {
      debouncedSave(newOptions);
    }
  };

  if (isLoading) {
    return (
      <div className="tw-flex tw-items-center tw-justify-center tw-py-4">
        <div className="tw-animate-spin tw-rounded-full tw-h-5 tw-w-5 tw-border-b-2 tw-border-gray-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="tw-text-sm tw-text-red-600">
        Failed to load options schema.
      </div>
    );
  }

  const schema = data?.schema || [];

  if (schema.length === 0) {
    return (
      <div className="tw-text-sm tw-text-gray-500 tw-italic">
        This processor has no configurable options.
      </div>
    );
  }

  return (
    <div className="tw-space-y-4">
      {schema.map((field) => (
        <OptionField
          key={field.name}
          field={field}
          value={localOptions[field.name]}
          onChange={(value, immediate) =>
            handleFieldChange(field.name, value, immediate)
          }
        />
      ))}
    </div>
  );
};

interface OptionFieldProps {
  field: OptionFieldSchema;
  value: unknown;
  onChange: (value: unknown, immediate?: boolean) => void;
}

const OptionField: React.FC<OptionFieldProps> = ({
  field,
  value,
  onChange,
}) => {
  const inputId = `option-field-${field.name}`;

  const renderField = (): React.ReactNode => {
    switch (field.type) {
      case "uuid": {
        const handleGenerateClick = (): void => {
          const newValue = uuidv4();
          onChange(newValue, true);
        };

        return (
          <div className="tw-flex tw-gap-2">
            <input
              id={inputId}
              type="text"
              value={(value as string) || ""}
              onChange={(e) => onChange(e.target.value)}
              className="tw-block tw-w-full tw-rounded-md tw-border-gray-300 tw-shadow-sm focus:tw-border-blue-500 focus:tw-ring-blue-500 tw-text-sm"
            />
            <button
              type="button"
              className="tw-inline-flex tw-items-center tw-rounded-md tw-border tw-border-gray-300 tw-bg-white tw-px-2.5 tw-py-1.5 tw-text-xs tw-font-medium tw-text-gray-700 hover:tw-bg-gray-50 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-offset-2 focus:tw-ring-blue-500"
              onClick={handleGenerateClick}
            >
              Generate ID
            </button>
          </div>
        );
      }

      case "choice": {
        const choiceOptions =
          field.choices?.map((c) => ({
            key: c.value,
            value: c.value,
            text: c.label,
          })) || [];

        return (
          <Dropdown
            options={choiceOptions}
            value={(value as string) || ""}
            onChange={(v) => onChange(v, true)}
            placeholder="Select..."
            clearable={!field.required}
          />
        );
      }

      case "boolean":
        return (
          <Toggle
            checked={Boolean(value)}
            setChecked={(checked) => onChange(checked, true)}
          />
        );

      case "integer":
        return (
          <input
            id={inputId}
            type="number"
            value={(value as number) ?? ""}
            onChange={(e) => {
              const numValue = e.target.value
                ? parseInt(e.target.value, 10)
                : null;
              onChange(numValue);
            }}
            className="tw-block tw-w-full tw-rounded-md tw-border-gray-300 tw-shadow-sm focus:tw-border-blue-500 focus:tw-ring-blue-500 tw-text-sm"
          />
        );

      case "text":
      default:
        return (
          <input
            id={inputId}
            type="text"
            value={(value as string) || ""}
            onChange={(e) => onChange(e.target.value)}
            className="tw-block tw-w-full tw-rounded-md tw-border-gray-300 tw-shadow-sm focus:tw-border-blue-500 focus:tw-ring-blue-500 tw-text-sm"
          />
        );
    }
  };

  return (
    <div>
      <label
        htmlFor={inputId}
        className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-1"
      >
        {field.label}
        {field.required && <span className="tw-text-red-500 tw-ml-1">*</span>}
      </label>
      {renderField()}
      {field.help_text && (
        <p className="tw-mt-1 tw-text-xs tw-text-gray-500">{field.help_text}</p>
      )}
    </div>
  );
};
